import styled, { css, keyframes } from "styled-components";

import Button from "../../components/Buttons/Button";
import {
  ModalBodyBase,
  ModalCloseButtonBase,
  ModalFooterBase,
  ModalHeaderBase,
  ModalOverlayBase,
  ModalSurfaceBase,
} from "../../components/Modals/ModalPrimitives";
import { withAlpha } from "../../styles/utils";

export const Overlay = styled(ModalOverlayBase)``;

export const Surface = styled(ModalSurfaceBase)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 20px 52px ${({ theme }) => withAlpha(theme.colors.ink, 0.22)};
`;

export const Header = styled(ModalHeaderBase)`
  gap: 12px;
  padding: 18px 20px 14px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.45;
    letter-spacing: -0.01em;
  }
`;

export const CloseButton = styled(ModalCloseButtonBase)`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};

  &:hover:not(:disabled) {
    background: ${({ theme }) => withAlpha(theme.colors.ink, 0.05)};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

export const ContextBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 0 20px 4px;
  padding: 12px 20px 14px;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.ink, 0.14)};
  border-radius: 12px;
`;

export const ContextCopy = styled.div`
  min-width: 0;
`;

export const StudentName = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CourseName = styled.div`
  margin-top: 3px;
  overflow: hidden;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ContextDate = styled.div`
  margin-top: 5px;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
`;

export const StatusChip = styled.span<{ $tone: "active" | "ended" | "completed" }>`
  display: inline-flex;
  align-items: center;
  height: 25px;
  flex: 0 0 auto;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;

  ${({ $tone, theme }) =>
    $tone === "ended"
      ? css`
          background: ${withAlpha(theme.colors.dangerText, 0.1)};
          color: ${theme.colors.dangerText};
        `
      : $tone === "completed"
        ? css`
            background: ${withAlpha(theme.colors.ink, 0.06)};
            color: ${withAlpha(theme.colors.ink, 0.64)};
          `
        : css`
            background: ${withAlpha(theme.colors.success, 0.14)};
            color: ${theme.colors.successText};
          `}
`;

export const Body = styled(ModalBodyBase)`
  min-height: 0;
  padding: 14px 20px 4px;
  overflow-y: auto;
`;

export const OrdersSection = styled.section``;

export const SectionTitle = styled.h3`
  margin: 0 0 10px;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
`;

export const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const OrderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 13px;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.ink, 0.14)};
  border-radius: 11px;
`;

export const OrderIcon = styled.span<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border-radius: 9px;
  background: ${({ theme, $danger }) =>
    $danger ? withAlpha(theme.colors.dangerText, 0.09) : withAlpha(theme.colors.brand, 0.08)};
  color: ${({ theme, $danger }) => ($danger ? theme.colors.dangerText : theme.colors.brand)};

  svg {
    width: 15px;
    height: 19px;
  }
`;

export const OrderMeta = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

export const OrderTitle = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OrderMetaText = styled.div`
  margin-top: 3px;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
`;

export const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  flex: 0 0 auto;
  padding: 0 11px;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.ink, 0.14)};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  text-decoration: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => withAlpha(theme.colors.brand, 0.08)};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
  }

  @media (max-width: 420px) {
    width: 32px;
    padding: 0;
    font-size: 0;
    gap: 0;
  }
`;

export const StateBlock = styled.div`
  padding: 22px 16px;
  border: 1px dashed ${({ theme }) => withAlpha(theme.colors.ink, 0.14)};
  border-radius: 12px;
  text-align: center;
`;

export const StateText = styled.div<{ $danger?: boolean }>`
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.dangerText : withAlpha(theme.colors.ink, 0.42)};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
`;

const shine = keyframes`
  to { background-position: -200% 0; }
`;

export const OrderSkeleton = styled.div`
  height: 56px;
  border-radius: 11px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => withAlpha(theme.colors.ink, 0.05)},
    ${({ theme }) => withAlpha(theme.colors.ink, 0.1)},
    ${({ theme }) => withAlpha(theme.colors.ink, 0.05)}
  );
  background-size: 200% 100%;
  animation: ${shine} 1.2s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Confirmation = styled.div`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.dangerText, 0.3)};
  border-left-width: 3px;
  border-radius: 4px 12px 12px 4px;
  background: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.05)};
`;

export const ConfirmationLead = styled.div`
  margin-bottom: 7px;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.45;
`;

export const ConfirmationText = styled.div`
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.55;
`;

export const ConfirmationList = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;

  li {
    margin-bottom: 5px;
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const demoButtonGeometry = css`
  width: auto;
  min-width: 0;
  max-width: none;
  height: 38px;
  padding: 0 15px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
  }
`;

export const QuietButton = styled(Button)`
  ${demoButtonGeometry}
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.ink, 0.14)};
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.14)};
    background: ${({ theme }) => withAlpha(theme.colors.ink, 0.05)};
    color: ${({ theme }) => theme.colors.ink};
  }
`;

export const DangerQuietButton = styled(Button)`
  ${demoButtonGeometry}
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.dangerText, 0.35)};
  background: transparent;
  color: ${({ theme }) => theme.colors.dangerText};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.35)};
    background: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.07)};
    color: ${({ theme }) => theme.colors.dangerText};
  }
`;

export const DangerButton = styled(Button)`
  ${demoButtonGeometry}
  border: 1px solid transparent;
  background: ${({ theme }) => theme.colors.dangerText};
  color: ${({ theme }) => theme.colors.white};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.dangerDeep};
  }

  > span[aria-hidden="true"] {
    width: 15px;
    height: 15px;
    border-width: 2px;
  }
`;

export const RetryButton = styled(QuietButton)`
  margin-top: 12px;
`;

export const Footer = styled(ModalFooterBase)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 16px 20px 18px;
  border-top: 1px solid ${({ theme }) => withAlpha(theme.colors.ink, 0.08)};

  @media (max-width: 420px) {
    flex-wrap: wrap;
  }
`;

export const FooterRight = styled.span`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;
